import { Fragment, useState } from 'react'
import {
  Alert,
  Badge,
  Card,
  Group,
  ScrollArea,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import { AreaChart, BarChart } from '@mantine/charts'
import { useQuery } from '@tanstack/react-query'
import { getWafDashboard } from '@/api/waf'
import type { WafDashboard, WafKpi } from '@/api/waf'

// ─── Helper: variação vs período anterior ────────────────────────────────────

function Delta({ pct }: { pct: number | null | undefined }) {
  if (pct === null || pct === undefined) return <Text size="xs" c="dimmed">—</Text>
  const up = pct >= 0
  return (
    <Badge size="xs" variant="light" color={up ? 'green' : 'red'}>
      {up ? '↑' : '↓'} {Math.abs(pct)}% vs período anterior
    </Badge>
  )
}

// ─── Helper: card de KPI ──────────────────────────────────────────────────────

interface KpiCardProps {
  label: string
  kpi: WafKpi
  suffix?: string
}

function KpiCard({ label, kpi, suffix }: KpiCardProps) {
  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap={4}>
        <Text size="xs" c="dimmed">{label}</Text>
        <Text fw={700} size="xl">
          {kpi.value.toLocaleString('pt-BR')}
          {suffix ? <Text component="span" size="sm" c="dimmed" ml={4}>{suffix}</Text> : null}
        </Text>
        <Delta pct={kpi.delta_pct} />
      </Stack>
    </Card>
  )
}

// ─── Helper: heatmap de tráfego ───────────────────────────────────────────────

const DOW = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']

function Heatmap({ cells }: { cells: { dow: number; hour: number; count: number }[] }) {
  const map = new Map(cells.map((c) => [`${c.dow}-${c.hour}`, c.count]))
  const max = Math.max(1, ...cells.map((c) => c.count))
  return (
    <ScrollArea>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '40px repeat(24, 18px)',
          gap: 2,
          alignItems: 'center',
        }}
      >
        {/* Linha de horas */}
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <Text key={`h${h}`} size="9px" ta="center">{h}</Text>
        ))}

        {/* Linhas de dias */}
        {DOW.map((label, dow) => (
          <Fragment key={`row-${dow}`}>
            <Text size="xs">{label}</Text>
            {Array.from({ length: 24 }, (_, h) => {
              const v = map.get(`${dow}-${h}`) ?? 0
              const a = v === 0 ? 0 : 0.15 + 0.85 * (v / max)
              return (
                <Tooltip key={`${dow}-${h}`} label={`${label} ${h}h: ${v.toLocaleString('pt-BR')}`} withArrow>
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 3,
                      background:
                        v === 0
                          ? 'var(--mantine-color-gray-1)'
                          : `rgba(220,38,38,${a})`,
                    }}
                  />
                </Tooltip>
              )
            })}
          </Fragment>
        ))}
      </div>
    </ScrollArea>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface WafDashboardPanelProps {
  wafId: number
  window?: '24h' | '7d' | '30d'
  onWindowChange?: (w: '24h' | '7d' | '30d') => void
  host?: string
}

/**
 * O resumo do período só é utilizável quando TODOS os KPIs que os cartões leem estão
 * presentes.
 *
 * A rota do dashboard nunca responde 500: painel que estoura no Logs Insights vira valor
 * vazio e o nome entra em `errors`. Para `kpis` esse vazio era `{}` — e `{}` é **truthy**,
 * então `data.kpis && ...` passava e o `total` inexistente estourava
 * `Cannot read properties of undefined (reading 'value')`. Erro `9536dc3c`, produção,
 * 20/08/2026, ao trocar a janela para 30 dias (a consulta mais pesada das oito).
 */
export function resumoUtilizavel(
  kpis: WafDashboard['kpis'] | undefined,
): kpis is NonNullable<WafDashboard['kpis']> {
  if (!kpis) return false
  return (['total', 'blocked', 'allowed', 'countries', 'ips'] as const).every(
    (k) => typeof kpis[k]?.value === 'number',
  )
}

/**
 * "Nenhum acesso na janela" é uma AFIRMAÇÃO sobre o tráfego, e só pode ser feita quando o
 * resumo foi lido de verdade.
 *
 * Com o painel de KPI falhado, dizer "nenhum acesso" seria a mesma mentira que
 * `OverviewTab.estados` e `MetricsTab.estados` guardam no backend do banco: silêncio de
 * consulta virando atestado. Ausência de dado não é ausência de tráfego.
 */
export function semAcessosNaJanela(
  data: Pick<WafDashboard, 'logging_enabled' | 'kpis' | 'errors'> | undefined,
): boolean {
  if (!data || data.logging_enabled === false) return false
  if (data.errors?.includes('kpis')) return false
  if (!resumoUtilizavel(data.kpis)) return false
  return data.kpis.total.value === 0
}

/**
 * Explica a ausência de comparação em vez de deixar seis travessões mudos.
 *
 * `<Delta pct={null}>` renderiza '—' tanto para "base zero" quanto para "não deu para
 * ler o período anterior" — informações diferentes com a mesma aparência. Em 30 dias o
 * segundo caso é permanente, não eventual: o período anterior cai entre 30 e 60 dias
 * atrás e o log group retém 30. Dizer isso uma vez vale mais que repetir o traço.
 */
export function motivoSemComparacao(
  reason: 'retention' | 'creation' | 'unreadable' | null,
): string {
  if (reason === 'retention')
    return 'Sem comparação com o período anterior: a retenção de logs deste WAF não alcança tão atrás.'
  if (reason === 'creation')
    return 'Sem comparação com o período anterior: o log group é mais novo que essa janela.'
  return 'Sem comparação com o período anterior: a consulta ao período anterior não respondeu.'
}

export function WafDashboardPanel({ wafId, window: windowProp, onWindowChange, host }: WafDashboardPanelProps) {
  const [internalWindow, setInternalWindow] = useState<'24h' | '7d' | '30d'>('7d')
  const window = windowProp ?? internalWindow
  const setWindow = onWindowChange ?? setInternalWindow

  const q = useQuery({
    queryKey: ['waf-dashboard', wafId, window, host ?? 'ALL'],
    queryFn: () => getWafDashboard(wafId, window, host),
  })

  // Resultado vazio para o filtro de domínio: logging habilitado, resumo LIDO, e sem
  // nenhum acesso na janela. A leitura do resumo é condição, não detalhe — ver
  // `semAcessosNaJanela`.
  const hasNoRows = semAcessosNaJanela(q.data)

  return (
    <Stack gap="md">
      {/* Filtro de domínio ativo + seletor de janela */}
      <Group justify="space-between" align="center">
        {host ? (
          <Badge variant="light" color="blue">Filtrado por: {host}</Badge>
        ) : (
          <span />
        )}
        <SegmentedControl
          value={window}
          onChange={(v) => setWindow(v as '24h' | '7d' | '30d')}
          data={[
            { label: '24h', value: '24h' },
            { label: '7 dias', value: '7d' },
            { label: '30 dias', value: '30d' },
          ]}
          size="xs"
        />
      </Group>

      {/* Loading */}
      {q.isLoading && (
        <Stack gap="sm">
          <Skeleton h={120} />
          <Skeleton h={120} />
          <Skeleton h={120} />
        </Stack>
      )}

      {/* Erro */}
      {q.isError && !q.isLoading && (
        <Alert color="red">Erro ao carregar o dashboard.</Alert>
      )}

      {/* Logging desabilitado */}
      {q.data && q.data.logging_enabled === false && (
        <Alert color="gray">
          Logging do CloudWatch não está habilitado neste WAF — visão executiva indisponível.
        </Alert>
      )}

      {/* Sem dados para o filtro de domínio na janela */}
      {hasNoRows && (
        <Alert color="gray">Sem dados para este domínio na janela.</Alert>
      )}

      {/* Conteúdo principal */}
      {q.data && q.data.logging_enabled !== false && !hasNoRows && (() => {
        const data = q.data

        return (
          <Stack gap="lg">
            {/* ── KPIs ──────────────────────────────────────────────────────── */}
            {!resumoUtilizavel(data.kpis) && data.errors?.includes('kpis') && (
              <Card withBorder padding="md" radius="md">
                <Text fw={600} mb="sm">Resumo do período</Text>
                <Text size="sm" c="dimmed">
                  Não foi possível carregar o resumo desta janela — a consulta ao Logs
                  Insights não respondeu. Isso não significa ausência de tráfego. Tente
                  uma janela menor ou recarregue.
                </Text>
              </Card>
            )}
            {resumoUtilizavel(data.kpis) && (
              <Card withBorder padding="md" radius="md">
                <Text fw={600} mb="sm">Resumo do período</Text>
                {data.errors?.includes('kpis') && (
                  <Text size="xs" c="dimmed" mb="xs">dados parciais</Text>
                )}
                <SimpleGrid cols={{ base: 2, md: 5 }}>
                  <KpiCard label="Total de acessos" kpi={data.kpis.total} />
                  <KpiCard
                    label="Bloqueadas"
                    kpi={data.kpis.blocked}
                    suffix={`(${data.kpis.blocked_pct}%)`}
                  />
                  <KpiCard label="Permitidas" kpi={data.kpis.allowed} />
                  <KpiCard label="Países distintos" kpi={data.kpis.countries} />
                  <KpiCard label="IPs distintos" kpi={data.kpis.ips} />
                </SimpleGrid>
                {data.kpis.comparison?.available === false && (
                  <Text size="xs" c="dimmed" mt="sm">
                    {motivoSemComparacao(data.kpis.comparison.reason)}
                  </Text>
                )}
              </Card>
            )}

            {/* ── Linha do tempo ────────────────────────────────────────────── */}
            {data.timeseries && data.timeseries.length > 0 && (
              <Card withBorder padding="md" radius="md">
                <Text fw={600} mb="sm">Tráfego ao longo do tempo</Text>
                {data.errors?.includes('timeseries') && (
                  <Text size="xs" c="dimmed" mb="xs">dados parciais</Text>
                )}
                <AreaChart
                  h={240}
                  data={data.timeseries}
                  dataKey="ts"
                  series={[
                    { name: 'allowed', label: 'Acessos', color: 'blue.5' },
                    { name: 'blocked', label: 'Bloqueios', color: 'red.6' },
                  ]}
                  curveType="monotone"
                  withLegend
                />
              </Card>
            )}

            {/* ── Tipos de bloqueio ─────────────────────────────────────────── */}
            {data.block_types && data.block_types.length > 0 && (
              <Card withBorder padding="md" radius="md">
                <Text fw={600} mb="sm">Tipos de bloqueio mais frequentes</Text>
                {data.errors?.includes('block_types') && (
                  <Text size="xs" c="dimmed" mb="xs">dados parciais</Text>
                )}
                <BarChart
                  h={260}
                  data={data.block_types}
                  dataKey="rule"
                  series={[{ name: 'count', label: 'Bloqueios', color: 'red.6' }]}
                  orientation="vertical"
                />
              </Card>
            )}

            {/* ── Países ────────────────────────────────────────────────────── */}
            {((data.countries_access && data.countries_access.length > 0) ||
              (data.countries_blocked && data.countries_blocked.length > 0)) && (
              <Card withBorder padding="md" radius="md">
                <Text fw={600} mb="sm">Países</Text>
                {(data.errors?.includes('countries_access') || data.errors?.includes('countries_blocked')) && (
                  <Text size="xs" c="dimmed" mb="xs">dados parciais</Text>
                )}
                <SimpleGrid cols={{ base: 1, md: 2 }}>
                  {data.countries_access && data.countries_access.length > 0 && (
                    <Stack gap={4}>
                      <Text size="sm" fw={500}>Top países por acesso</Text>
                      <BarChart
                        h={220}
                        data={data.countries_access}
                        dataKey="country"
                        series={[{ name: 'count', label: 'Acessos', color: 'blue.5' }]}
                        orientation="vertical"
                      />
                    </Stack>
                  )}
                  {data.countries_blocked && data.countries_blocked.length > 0 && (
                    <Stack gap={4}>
                      <Text size="sm" fw={500}>Top países bloqueados</Text>
                      <BarChart
                        h={220}
                        data={data.countries_blocked}
                        dataKey="country"
                        series={[{ name: 'count', label: 'Bloqueios', color: 'red.6' }]}
                        orientation="vertical"
                      />
                    </Stack>
                  )}
                </SimpleGrid>
              </Card>
            )}

            {/* ── Heatmap ────────────────────────────────────────────────────── */}
            {data.heatmap && data.heatmap.length > 0 && (
              <Card withBorder padding="md" radius="md">
                <Text fw={600} mb="sm">Heatmap de tráfego (por hora/dia — horário de Brasília)</Text>
                {data.errors?.includes('heatmap') && (
                  <Text size="xs" c="dimmed" mb="xs">dados parciais</Text>
                )}
                <Heatmap cells={data.heatmap} />
              </Card>
            )}

            {/* ── Top URIs ──────────────────────────────────────────────────── */}
            {data.top_uris && data.top_uris.length > 0 && (
              <Card withBorder padding="md" radius="md">
                <Text fw={600} mb="sm">Top URIs bloqueadas</Text>
                {data.errors?.includes('top_uris') && (
                  <Text size="xs" c="dimmed" mb="xs">dados parciais</Text>
                )}
                <Table striped withTableBorder style={{ maxWidth: 600 }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>URI</Table.Th>
                      <Table.Th style={{ width: 100 }}>Bloqueios</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.top_uris.map((item, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Table.Tr key={i}>
                        <Table.Td>
                          <Text size="xs" ff="monospace">{item.key}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs">{item.count.toLocaleString('pt-BR')}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Card>
            )}

            {/* ── Top IPs bloqueados ────────────────────────────────────────── */}
            {data.top_ips && data.top_ips.length > 0 && (
              <Card withBorder padding="md" radius="md">
                <Text fw={600} mb="sm">Top IPs bloqueados</Text>
                {data.errors?.includes('top_ips') && (
                  <Text size="xs" c="dimmed" mb="xs">dados parciais</Text>
                )}
                <Table striped withTableBorder style={{ maxWidth: 400 }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>IP</Table.Th>
                      <Table.Th style={{ width: 100 }}>Bloqueios</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.top_ips.map((item, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Table.Tr key={i}>
                        <Table.Td>
                          <Text size="xs" ff="monospace">{item.key}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs">{item.count.toLocaleString('pt-BR')}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Card>
            )}
          </Stack>
        )
      })()}
    </Stack>
  )
}
